import React, { useCallback, useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import api from "services/api"
import SimpleHeader from "components/Headers/SimpleHeader.js"
import Loading from "components/Modals/Loading"
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Modal,
  Row,
  Table,
} from "reactstrap"

const STATUS_LABEL = {
  pendente: "Aguardando",
  processando: "Processando",
  concluido: "Concluído",
  erro: "Erro",
}

const STATUS_COLOR = {
  pendente: "warning",
  processando: "info",
  concluido: "success",
  erro: "danger",
}

const EMPTY_NEW_VIDEO = { url: "", titulo: "", moduloSap: "", clienteId: "" }
const EMPTY_EDIT_FORM = { _id: null, url: "", titulo: "", moduloSap: "", clienteId: "" }

const normalize = text =>
  String(text ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")

const GLOBAL_OPTION_LABEL = "— Global (todos os clientes) —"

export default function KnowledgeVideos() {
  const [videos, setVideos] = useState([])
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [newVideo, setNewVideo] = useState(EMPTY_NEW_VIDEO)
  const [addError, setAddError] = useState("")
  const [search, setSearch] = useState("")
  const [sortBy, setSortBy] = useState(null)
  const [sortDir, setSortDir] = useState("asc")
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editForm, setEditForm] = useState(EMPTY_EDIT_FORM)
  const [editError, setEditError] = useState("")
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [videoToDelete, setVideoToDelete] = useState(null)
  const [reprocessing, setReprocessing] = useState(false)
  const pollRef = useRef(null)

  const clienteNomeById = customers.reduce((acc, customer) => {
    acc[customer._id] = customer.shortName || customer.fullName
    return acc
  }, {})

  const clienteNome = video => (video.clienteId ? clienteNomeById[video.clienteId] || "Cliente removido" : "Global")

  const SORT_ACCESSORS = {
    titulo: video => video.titulo || "",
    moduloSap: video => video.moduloSap || "",
    status: video => STATUS_LABEL[video.status] || video.status || "",
    clienteId: video => clienteNome(video),
  }

  const fetchCustomers = useCallback(async () => {
    const response = await api.get("customer/all")
    setCustomers(response.data)
  }, [])

  const fetchVideos = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true)
    try {
      const response = await api.get("knowledgeSource/all?sourceType=video")
      setVideos(response.data)
    } finally {
      if (showLoading) setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchVideos()
    fetchCustomers()
  }, [fetchVideos, fetchCustomers])

  useEffect(() => {
    const hasActiveJob = videos.some(
      video => video.status === "pendente" || video.status === "processando"
    )

    if (hasActiveJob && !pollRef.current) {
      pollRef.current = setInterval(() => fetchVideos(false), 8000)
    }

    if (!hasActiveJob && pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [videos, fetchVideos])

  const handleAddVideo = async e => {
    e.preventDefault()
    setSaving(true)
    setAddError("")

    try {
      await api.post("knowledgeSource/create/video", newVideo)
      setNewVideo(EMPTY_NEW_VIDEO)
      await fetchVideos()
    } catch (err) {
      setAddError(err?.response?.data?.error || "Erro ao adicionar o vídeo.")
    } finally {
      setSaving(false)
    }
  }

  const handleReprocessErrors = async () => {
    setReprocessing(true)
    try {
      await api.post("knowledgeSource/reprocess-errors?sourceType=video")
      await fetchVideos(false)
    } finally {
      setReprocessing(false)
    }
  }

  const openEditModal = video => {
    setEditForm({
      _id: video._id,
      url: video.url,
      titulo: video.titulo,
      moduloSap: video.moduloSap || "",
      clienteId: video.clienteId || "",
    })
    setEditError("")
    setEditModalOpen(true)
  }

  const closeEditModal = () => {
    if (saving) return
    setEditModalOpen(false)
  }

  const handleEditSave = async e => {
    e.preventDefault()
    setSaving(true)
    setEditError("")

    try {
      await api.put(`knowledgeSource/update/${editForm._id}`, {
        url: editForm.url,
        titulo: editForm.titulo,
        moduloSap: editForm.moduloSap,
        clienteId: editForm.clienteId,
      })
      setEditModalOpen(false)
      await fetchVideos()
    } catch (err) {
      setEditError(err?.response?.data?.error || "Erro ao salvar o vídeo.")
    } finally {
      setSaving(false)
    }
  }

  const errorCount = videos.filter(video => video.status === "erro").length

  const term = normalize(search.trim())
  const filteredVideos = term
    ? videos.filter(
        video =>
          normalize(video.titulo).includes(term) ||
          normalize(video.url).includes(term) ||
          normalize(video.videoId).includes(term) ||
          normalize(video.moduloSap).includes(term) ||
          normalize(clienteNome(video)).includes(term)
      )
    : videos

  const sortedVideos = sortBy
    ? [...filteredVideos].sort((a, b) => {
        const result = SORT_ACCESSORS[sortBy](a).localeCompare(SORT_ACCESSORS[sortBy](b), "pt", {
          sensitivity: "base",
        })
        return sortDir === "asc" ? result : -result
      })
    : filteredVideos

  const toggleSort = column => {
    if (sortBy !== column) {
      setSortBy(column)
      setSortDir("asc")
    } else if (sortDir === "asc") {
      setSortDir("desc")
    } else {
      setSortBy(null)
      setSortDir("asc")
    }
  }

  const sortIndicator = column => {
    if (sortBy !== column) return null
    return sortDir === "asc" ? " ▲" : " ▼"
  }

  const openDeleteModal = video => {
    setVideoToDelete(video)
    setDeleteModalOpen(true)
  }

  const confirmDelete = async () => {
    setSaving(true)
    try {
      await api.delete(`knowledgeSource/delete/${videoToDelete._id}`)
      setDeleteModalOpen(false)
      setVideoToDelete(null)
      await fetchVideos()
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <SimpleHeader
        name="Vídeos"
        items={[{ level: "nav", name: "Base de Conhecimento do SmartChat" }]}
      />
      <Container className="mt--6" fluid>
        <Row>
          <Col xl="12">
            <Link to="/admin/knowledge-base" className="text-muted small">
              <i className="ni ni-bold-left" /> Voltar pra Base de Conhecimento
            </Link>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col xl="12">
            <Card>
              <CardHeader>
                <h5 className="h3 mb-0">Adicionar vídeo</h5>
              </CardHeader>
              <CardBody>
                <Form onSubmit={handleAddVideo}>
                  {addError && <div className="text-danger mb-3">{addError}</div>}
                  <Row>
                    <Col md="4">
                      <FormGroup>
                        <label className="form-control-label">Link do YouTube</label>
                        <Input
                          required
                          type="url"
                          placeholder="https://www.youtube.com/watch?v=..."
                          value={newVideo.url}
                          disabled={saving}
                          onChange={e => setNewVideo({ ...newVideo, url: e.target.value })}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <label className="form-control-label">Título</label>
                        <Input
                          required
                          type="text"
                          placeholder="Título do vídeo"
                          value={newVideo.titulo}
                          disabled={saving}
                          onChange={e => setNewVideo({ ...newVideo, titulo: e.target.value })}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <label className="form-control-label">Módulo SAP</label>
                        <Input
                          type="text"
                          placeholder="Ex: Financeiro, Compras..."
                          value={newVideo.moduloSap}
                          disabled={saving}
                          onChange={e => setNewVideo({ ...newVideo, moduloSap: e.target.value })}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="2" className="d-flex align-items-end">
                      <FormGroup className="w-100">
                        <Button
                          color="primary"
                          type="submit"
                          disabled={saving}
                          block
                          style={{ whiteSpace: "nowrap" }}
                        >
                          {saving ? "Adicionando..." : "Adicionar"}
                        </Button>
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="4">
                      <FormGroup>
                        <label className="form-control-label">Cliente</label>
                        <Input
                          type="select"
                          value={newVideo.clienteId}
                          disabled={saving}
                          onChange={e => setNewVideo({ ...newVideo, clienteId: e.target.value })}
                        >
                          <option value="">{GLOBAL_OPTION_LABEL}</option>
                          {customers.map(customer => (
                            <option key={customer._id} value={customer._id}>
                              {customer.shortName || customer.fullName}
                            </option>
                          ))}
                        </Input>
                        <div className="text-muted small mt-1">
                          Deixe em "Global" pra conteúdo genérico (visível pra
                          todo mundo). Escolha um cliente pra restringir esse
                          vídeo só a ele.
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col xl="12">
            <Card>
              <CardHeader>
                <Row>
                  <Col xs="6">
                    <h5 className="h3 mb-0">Vídeos da base de conhecimento</h5>
                  </Col>
                  <Col
                    xs="6"
                    className="d-flex align-items-center justify-content-end"
                  >
                    {errorCount > 0 && (
                      <Button
                        color="warning"
                        size="sm"
                        className="mr-3 mb-0"
                        disabled={reprocessing}
                        onClick={handleReprocessErrors}
                      >
                        {reprocessing ? "Reprocessando..." : "Reprocessar vídeos"}
                      </Button>
                    )}
                    <span className="text-muted">
                      {term
                        ? `${filteredVideos.length} de ${videos.length} vídeo(s)`
                        : `${videos.length} vídeo(s)`}
                    </span>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <FormGroup className="mb-3">
                  <Input
                    type="text"
                    placeholder="Pesquisar por título, módulo ou link do vídeo..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                  />
                </FormGroup>
                <Table responsive>
                  <thead>
                    <tr>
                      <th
                        style={{ cursor: "pointer", userSelect: "none" }}
                        onClick={() => toggleSort("titulo")}
                      >
                        Título{sortIndicator("titulo")}
                      </th>
                      <th
                        style={{ cursor: "pointer", userSelect: "none" }}
                        onClick={() => toggleSort("moduloSap")}
                      >
                        Módulo SAP{sortIndicator("moduloSap")}
                      </th>
                      <th
                        style={{ cursor: "pointer", userSelect: "none" }}
                        onClick={() => toggleSort("clienteId")}
                      >
                        Cliente{sortIndicator("clienteId")}
                      </th>
                      <th
                        style={{ cursor: "pointer", userSelect: "none" }}
                        onClick={() => toggleSort("status")}
                      >
                        Status{sortIndicator("status")}
                      </th>
                      <th>Chunks</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedVideos.map(video => (
                      <tr key={video._id}>
                        <td>
                          <a href={video.url} target="_blank" rel="noopener noreferrer">
                            {video.titulo}
                          </a>
                        </td>
                        <td>{video.moduloSap || "-"}</td>
                        <td>
                          <Badge color={video.clienteId ? "info" : "secondary"}>
                            {clienteNome(video)}
                          </Badge>
                        </td>
                        <td>
                          <Badge color={STATUS_COLOR[video.status] || "secondary"}>
                            {STATUS_LABEL[video.status] || video.status}
                          </Badge>
                          {video.status === "erro" && video.errorMessage && (
                            <div className="text-muted small mt-1">
                              {video.errorMessage}
                            </div>
                          )}
                        </td>
                        <td>{video.chunkCount || 0}</td>
                        <td>
                          <Button
                            color="secondary"
                            size="sm"
                            disabled={video.status === "processando"}
                            onClick={() => openEditModal(video)}
                          >
                            Editar
                          </Button>{" "}
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => openDeleteModal(video)}
                          >
                            Excluir
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {videos.length === 0 && !loading && (
                      <tr>
                        <td colSpan="6" className="text-center text-muted">
                          Nenhum vídeo cadastrado ainda.
                        </td>
                      </tr>
                    )}
                    {videos.length > 0 && filteredVideos.length === 0 && (
                      <tr>
                        <td colSpan="6" className="text-center text-muted">
                          Nenhum vídeo encontrado para "{search}".
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal className="modal-dialog-centered" isOpen={editModalOpen} autoFocus={false}>
        <div className="modal-header">
          <h5 className="modal-title">Editar vídeo</h5>
          <button
            aria-label="Close"
            className="close"
            type="button"
            onClick={closeEditModal}
            disabled={saving}
          >
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <Form onSubmit={handleEditSave}>
          <div className="modal-body">
            {editError && <div className="text-danger mb-3">{editError}</div>}
            <FormGroup>
              <label className="form-control-label">Link do YouTube</label>
              <Input
                required
                type="url"
                placeholder="https://www.youtube.com/watch?v=..."
                value={editForm.url}
                disabled={saving}
                onChange={e => setEditForm({ ...editForm, url: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <label className="form-control-label">Título</label>
              <Input
                required
                type="text"
                placeholder="Título do vídeo"
                value={editForm.titulo}
                disabled={saving}
                onChange={e => setEditForm({ ...editForm, titulo: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <label className="form-control-label">Módulo SAP</label>
              <Input
                type="text"
                placeholder="Ex: Financeiro, Compras, Vendas..."
                value={editForm.moduloSap}
                disabled={saving}
                onChange={e => setEditForm({ ...editForm, moduloSap: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <label className="form-control-label">Cliente</label>
              <Input
                type="select"
                value={editForm.clienteId}
                disabled={saving}
                onChange={e => setEditForm({ ...editForm, clienteId: e.target.value })}
              >
                <option value="">{GLOBAL_OPTION_LABEL}</option>
                {customers.map(customer => (
                  <option key={customer._id} value={customer._id}>
                    {customer.shortName || customer.fullName}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <div className="text-muted small">
              Alterar o link, o título ou o cliente reprocessa o vídeo do zero (apaga os
              trechos antigos e reingere a transcrição atualizada).
              <br />
              Alterar só o módulo SAP não reprocessa.
            </div>
          </div>
          <div className="modal-footer">
            <Button color="link" type="button" onClick={closeEditModal} disabled={saving}>
              Cancelar
            </Button>
            <Button color="primary" type="submit" disabled={saving}>
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        className="modal-dialog-centered"
        isOpen={deleteModalOpen}
        autoFocus={false}
      >
        <div className="modal-header">
          <h5 className="modal-title">Excluir vídeo</h5>
          <button
            aria-label="Close"
            className="close"
            type="button"
            onClick={() => setDeleteModalOpen(false)}
            disabled={saving}
          >
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <div className="modal-body">
          Tem certeza que deseja excluir o vídeo{" "}
          <strong>{videoToDelete?.titulo}</strong> da base de conhecimento? Essa
          ação remove o conteúdo do SmartChat imediatamente e não pode ser
          desfeita.
          {videoToDelete?.status === "processando" && (
            <div className="text-warning small mt-2">
              Este vídeo ainda está em processamento. Excluir agora não
              interrompe a chamada em andamento na hora, mas se ela terminar
              depois, o resultado é descartado automaticamente — nada fica
              órfão na base.
            </div>
          )}
        </div>
        <div className="modal-footer">
          <Button
            color="link"
            type="button"
            onClick={() => setDeleteModalOpen(false)}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button color="danger" type="button" onClick={confirmDelete} disabled={saving}>
            {saving ? "Excluindo..." : "Excluir"}
          </Button>
        </div>
      </Modal>

      <Loading modalOpened={loading} />
    </>
  )
}
